import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { EmergencyDisclaimer } from '../../components/EmergencyDisclaimer'
import type { SlotWindow, BookAppointmentResponse } from '../../lib/types'

const DAYS_AHEAD = 14

// availability_engine.py builds slots from the provider's work_start_hour/
// work_end_hour as literal wall-clock numbers stamped with a UTC tzinfo —
// there's no provider timezone concept anywhere in the schema yet. Formatting
// with the viewer's local timezone would silently shift "9" into whatever the
// browser's offset happens to be (e.g. 9am showing as 3am), so every display
// of these slot times must stay pinned to UTC to round-trip the hour the
// provider actually entered.
function dateKey(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
}

export default function BookAppointment() {
  const { providerId } = useParams<{ providerId: string }>()
  const [searchParams] = useSearchParams()
  const matchId = searchParams.get('matchId')
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [slots, setSlots] = useState<SlotWindow[]>([])
  const [selected, setSelected] = useState<SlotWindow | null>(null)
  const [booking, setBooking] = useState(false)
  const [confirmed, setConfirmed] = useState<BookAppointmentResponse | null>(null)

  useEffect(() => {
    async function run() {
      setLoading(true)
      setError(null)
      try {
        // Book Appointment can be reached before the patient has formally
        // selected this provider (from a match card, or from View Profile
        // which only knows the provider, not which match produced it) —
        // ensure the Case exists first. Idempotent: safe to call even if it
        // already does.
        await api('/patients/me/select-provider', {
          method: 'POST',
          body: JSON.stringify(matchId ? { match_id: matchId } : { provider_id: providerId }),
        })
        const start = new Date()
        const end = new Date()
        end.setDate(end.getDate() + DAYS_AHEAD)
        const data = await api(`/providers/${providerId}/available-slots?date_range_start=${start.toISOString().slice(0, 10)}&date_range_end=${end.toISOString().slice(0, 10)}`)
        setSlots(data.slots)
      } catch {
        setError("Could not load this provider's availability. Try again from your matches.")
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [providerId, matchId])

  async function confirmBooking() {
    if (!selected) return
    setBooking(true)
    setError(null)
    try {
      const result = await api('/patients/me/book-appointment', {
        method: 'POST',
        body: JSON.stringify({ provider_id: providerId, starts_at: selected.starts_at, ends_at: selected.ends_at }),
      })
      setConfirmed(result)
    } catch {
      setError('That slot is no longer available. Please choose another.')
      setSelected(null)
      // Re-fetch so the now-stale slot list reflects reality.
      const start = new Date()
      const end = new Date()
      end.setDate(end.getDate() + DAYS_AHEAD)
      api(`/providers/${providerId}/available-slots?date_range_start=${start.toISOString().slice(0, 10)}&date_range_end=${end.toISOString().slice(0, 10)}`)
        .then((data) => setSlots(data.slots))
        .catch(() => {})
    } finally {
      setBooking(false)
    }
  }

  if (loading) return <div className="page-loading">Loading available times…</div>

  if (confirmed) {
    return (
      <div>
        <div className="page-head"><h1>Appointment booked</h1></div>
        <EmergencyDisclaimer />
        <div className="card">
          <p>
            Your session is scheduled for{' '}
            <b>{new Date(confirmed.session_event.scheduled_at!).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })}</b>.
          </p>
          <p className="muted" style={{ fontSize: 13.5 }}>{confirmed.calendar_sync.note}</p>
          <button className="btn full" style={{ marginTop: 10 }} onClick={() => navigate('/patient')}>Back to my care</button>
        </div>
      </div>
    )
  }

  const grouped = slots.reduce<Record<string, SlotWindow[]>>((acc, s) => {
    const key = dateKey(s.starts_at)
    acc[key] = acc[key] || []
    acc[key].push(s)
    return acc
  }, {})

  return (
    <div>
      <div className="page-head">
        <button className="btn small outline" onClick={() => navigate(-1)} style={{ marginBottom: 10 }}>← Back</button>
        <h1>Book an appointment</h1>
        <div className="ph-sub">Choose a time that works for you</div>
      </div>
      <EmergencyDisclaimer />

      {error && <div className="form-error">{error}</div>}

      {slots.length === 0 ? (
        <div className="card">
          <p className="muted">No open times in the next {DAYS_AHEAD} days. Please check back later or contact the practice directly.</p>
        </div>
      ) : (
        <div>
          {Object.entries(grouped).map(([day, daySlots]) => (
            <div key={day} className="card" style={{ marginBottom: 12 }}>
              <h3 style={{ marginTop: 0 }}>{day}</h3>
              <div className="chip-list">
                {daySlots.map((s) => (
                  <span
                    key={s.starts_at}
                    className="chip"
                    style={{
                      cursor: 'pointer',
                      background: selected?.starts_at === s.starts_at ? 'var(--warm-terracotta, #C56A4B)' : '#F0EBE0',
                      color: selected?.starts_at === s.starts_at ? 'white' : 'inherit',
                    }}
                    onClick={() => setSelected(s)}
                  >
                    {timeLabel(s.starts_at)}
                  </span>
                ))}
              </div>
            </div>
          ))}

          <button className="btn full" disabled={!selected || booking} onClick={confirmBooking}>
            {booking ? 'Booking…' : selected ? `Confirm ${dateKey(selected.starts_at)} at ${timeLabel(selected.starts_at)}` : 'Select a time above'}
          </button>
        </div>
      )}
    </div>
  )
}
